import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import root from "../assets/styles/Root.module.css";
import tbl from "../assets/styles/ReferralTables.module.css";
import modal from "../assets/styles/TicketsCenter.module.css";

export default function BattlesPage() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [cases, setCases] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [caseId, setCaseId] = useState("");
  const [rounds, setRounds] = useState(10);
  const [isClosing, setIsClosing] = useState(false);
  const [mode, setMode] = useState("1v1");
  const [myFinished, setMyFinished] = useState([]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [battlesRes, casesRes, finishedRes] = await Promise.allSettled([
        authFetch("/api/battles/"),
        authFetch("/api/cases/?page_size=200"),
        authFetch("/api/battles/my-finished/?limit=10"),
      ]);
      if (battlesRes.status === "fulfilled" && battlesRes.value.ok) {
        const j = await battlesRes.value.json();
        setItems(Array.isArray(j) ? j : []);
      } else {
        setItems([]);
      }
      if (finishedRes.status === "fulfilled" && finishedRes.value.ok) {
        const j2 = await finishedRes.value.json();
        setMyFinished(Array.isArray(j2) ? j2 : []);
      } else {
        setMyFinished([]);
      }
      if (casesRes.status === "fulfilled" && casesRes.value.ok) {
        const j = await casesRes.value.json();
        // ответ может быть пагинированным {results:[]}
        setCases(Array.isArray(j) ? j : (Array.isArray(j?.results) ? j.results : []));
      } else {
        setCases([]);
      }
    } catch (e) {
      setError(e?.message || "Ошибка");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const caseIdToPrice = useMemo(() => {
    const map = new Map();
    for (const c of cases) {
      if (c?.id && c?.price_usd != null) map.set(c.id, Number(c.price_usd));
    }
    return map;
  }, [cases]);

  const onCreate = async (e) => {
    e?.preventDefault();
    if (!caseId) return;
    const c = cases.find(t => String(t.id) === String(caseId));
    const params = {
      case_id: Number(caseId),
      case_name: c?.name || "Кейс",
      case_price_usd: c?.price_usd || 0,
      rounds: Number(rounds) || 10,
      mode,
      host: user ? { id: user.id, email: user.email, username: user.username } : null,
    };
    const res = await authFetch("/api/battles/", { method: "POST", body: JSON.stringify({ case_id: params.case_id, rounds: params.rounds, mode }) });
    if (!res.ok) return;
    const b = await res.json();
    setIsClosing(true); setTimeout(()=>setCreateOpen(false), 150);
    navigate(`/battles/${b.id}`);
  };

  const unified = useMemo(() => {
    const withDerived = (items || []).map(b => {
      const price = (b.case_price_usd != null ? Number(b.case_price_usd) : (caseIdToPrice.get?.(b.case_id || b.case?.id) || 0));
      const cost = (Number(b.rounds || 0) * Number(price || 0)) || 0;
      const playersNow = Array.isArray(b.participants) ? b.participants.length : (b.opponent ? 2 : 1);
      const max = b.mode === "2v2" ? 4 : 2;
      const modeText = b.mode === "2v2" ? "2 vs 2" : "1 vs 1";
      return { ...b, _cost: cost, _playersNow: playersNow, _playersMax: max, _mode: modeText };
    });
    // Сортировка: сначала WAITING, потом ACTIVE, затем FINISHED (если попадутся)
    const order = { waiting: 0, active: 1, finished: 2 };
    return withDerived.sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));
  }, [items, caseIdToPrice]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2>Батлы</h2>
        <button className={root.btnPrimary} onClick={() => { setIsClosing(false); setCreateOpen(true); }}>Создать батл</button>
      </div>

      

      {error && <div style={{ color: "red", marginBottom: 12 }}>{error}</div>}

      <div className={root.card}>
        <div className={tbl.tableWrap}>
          <table className={tbl.table}>
            <thead>
              <tr>
                <th>Статус</th>
                <th>Раунды</th>
                <th>Сценарий батла</th>
                <th>Стоимость</th>
                <th>Игроки</th>
                <th>Действие</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}>Загрузка…</td></tr>
              ) : unified.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center" }}>Батлов пока нет</td></tr>
              ) : unified.map(b => {
                const isWaiting = b.status === "waiting";
                const isActive = b.status === "active";
                const canJoin = isWaiting && user && (b._playersNow < b._playersMax) && user.id !== b.host?.id;
                const actionLabel = canJoin ? "Участвовать" : "Смотреть";
                return (
                  <tr key={b.id}>
                    <td>{isWaiting ? "Ожидание" : isActive ? "Идёт" : b.status}</td>
                    <td>{b.rounds}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ fontWeight: 700 }}>{b.case_name || b.case_type_name}</div>
                        <div style={{ color: "#666" }}>{b._mode}</div>
                      </div>
                    </td>
                    <td>${Number(b._cost || 0).toFixed(2)}</td>
                    <td>{b._playersNow} / {b._playersMax}</td>
                    <td>
                      {canJoin ? (
                        <button
                          className={root.btnPrimary}
                          onClick={async (e) => {
                            e.preventDefault();
                            const res = await authFetch(`/api/battles/${b.id}/join/`, { method: "POST" });
                            if (res.ok) navigate(`/battles/${b.id}`);
                          }}
                        >
                          Участвовать
                        </button>
                      ) : (
                        <Link className={root.btn} to={`/battles/${b.id}`}>{actionLabel}</Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Завершённые батлы пользователя */}
      <div className={root.card} style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Мои завершённые батлы</h3>
        <div className={tbl.tableWrap}>
          <table className={tbl.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Кейс</th>
                <th>Раундов</th>
                <th>Режим</th>
                <th>Дата</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {myFinished.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center" }}>Нет завершённых батлов</td></tr>
              ) : myFinished.map(b => (
                <tr key={b.id}>
                  <td>{b.id}</td>
                  <td>{b.case?.name || b.case_name}</td>
                  <td>{b.rounds}</td>
                  <td>{b.mode === "2v2" ? "2 vs 2" : "1 vs 1"}</td>
                  <td>{b.finished_at ? new Date(b.finished_at).toLocaleString("ru-RU") : "—"}</td>
                  <td><Link className={root.btn} to={`/battles/${b.id}`}>Открыть</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Модалка создания батла */}
      {createOpen && (
        <div className={`${modal.backdrop} ${isClosing ? modal.backdropOut : modal.backdropIn}`} onClick={() => { setIsClosing(true); setTimeout(()=>setCreateOpen(false), 200); }}>
          <div className={`${modal.modal} ${isClosing ? modal.modalOut : modal.modalIn}`} onClick={(e)=>e.stopPropagation()}>
            <h3>Создать батл</h3>
            <div className={modal.field}>
              <label>Кейс</label>
              <select value={caseId} onChange={(e)=>setCaseId(e.target.value)}>
                <option value="">Выберите кейс…</option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} — ${Number(c.price_usd || 0).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>
            <div className={modal.field}>
              <label>Режим</label>
              <select value={mode} onChange={(e)=>setMode(e.target.value)}>
                <option value="1v1">1 vs 1</option>
                <option value="2v2">2 vs 2</option>
              </select>
            </div>
            <div className={modal.field}>
              <label>Количество раундов</label>
              <select value={rounds} onChange={(e)=>setRounds(e.target.value)}>
                {[5,10,20].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className={modal.modalActions}>
              <button className={root.btn} onClick={()=>{ setIsClosing(true); setTimeout(()=>setCreateOpen(false), 200); }}>Отмена</button>
              <button className={root.btnPrimary} onClick={onCreate} disabled={!caseId}>Создать</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


